import React from 'react';
import { GridTableApi } from '@api/GridTable.api';
import { GridTable, GridTableView, SaveGridTableViewColumnsDto, SaveGridTableViewDto, SaveGridTableViewFiltersDto } from '@api/models/gridTableApi.models';
import { useKeycloakContext } from '@common/context/keycloakContext';
import { ExternshipTrackerView } from '@common/fetches/useFetchGridTableByType';
import BasicModal from '@components/BasicModal';
import BottomMarginContentWrapper from '@components/BottomMarginContentWrapper';
import ReactHookFormsInput from '@components/Forms/ReactHookFormsInput';
import { useToast } from '@components/Toast';
import { Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useForm, FormProvider } from 'react-hook-form';

// Define the SaveGridTableViewModal component for saving and renaming views
interface FormValues {
  viewName: string;
}

export const SaveGridTableViewModal: React.FC<{
  open: boolean;
  handleClose: (e?: React.MouseEvent) => void;
  gridTable: GridTable | undefined;
  columns: GridColDef[];
  viewToRename?: GridTableView;
  getFilterColumns: () => SaveGridTableViewFiltersDto;
  getSaveGridTableViewColumns: () => SaveGridTableViewColumnsDto[];
  setGridTable: React.Dispatch<React.SetStateAction<GridTable | undefined>>;
  setCurrView: React.Dispatch<React.SetStateAction<ExternshipTrackerView | undefined>>;
}> = ({
  open,
  handleClose,
  viewToRename,
  gridTable,
  getFilterColumns,
  getSaveGridTableViewColumns,
  setGridTable,
  setCurrView
}) => {
  const { keycloakUser } = useKeycloakContext();
  const [isSaving, setIsSaving] = React.useState(false);
  const { openToast } = useToast();

  const methods = useForm<FormValues>({
    defaultValues: {
      viewName: viewToRename?.name || ''
    }
  });

  const { handleSubmit, reset } = methods;

  React.useEffect(() => {
    if (viewToRename) {
      reset({ viewName: viewToRename.name });
    } else {
      reset({ viewName: '' });
    }
  }, [viewToRename, reset]);

  const handleValidForm = async (data: FormValues): Promise<void> => {
    if (!gridTable) {
      const errorMessage = 'Could not save View: Grid table is not defined.';
      console.error(errorMessage);
      openToast(errorMessage);
      return;
    }

    try {
      setIsSaving(true);

      if (viewToRename) {
        // Renaming an existing view
        const updatedView = await GridTableApi.updateGridTableView(
          viewToRename.id,
          data.viewName
        );

        const newGridTable = {
          ...gridTable,
          customViews: gridTable.customViews.map((v) => {
            if (v.id === viewToRename.id) {
              return {
                ...v,
                name: updatedView.name
              };
            }
            return v;
          })
        };
        setGridTable(newGridTable);
        openToast('View renamed successfully');
      } else {
        // Creating a new view
        const postBody: SaveGridTableViewDto = {
          name: data.viewName,
          filters: getFilterColumns(),
          viewColumns: getSaveGridTableViewColumns(),
          ordinalNumber:
              [
                ...(gridTable?.defaultViews || []),
                ...(gridTable?.customViews || [])
              ].length + 1
        };

        const newView = await GridTableApi.saveGridTableView(
          gridTable.id,
          keycloakUser.sub,
          postBody
        );

        const newGridTable: GridTable = {
          ...gridTable,
          columns: newView.viewColumns
            .sort((a, b) => (a.ordinalNumber > b.ordinalNumber ? 1 : -1))
            .map((vc) => vc.column),
          customViews: [...(gridTable.customViews || []), newView]
        };

        // Set grid table first to avoid MUI select error where option isn't found
        setGridTable(newGridTable);
        setCurrView({ currView: newView.id });
        openToast('View saved successfully');
      }

      reset({ viewName: '' });
      handleClose();
    } catch (error) {
      console.error('Error saving view:', error);
      openToast('Error saving view. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveView = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(handleValidForm)();
  };

  return (
    <BasicModal
      open={open}
      handleClose={handleClose}
      title={viewToRename ? 'Rename View' : 'Save View'}
      primaryAction={{
        label: 'Save',
        action: handleSaveView,
        disabled: isSaving
      }}
      secondaryAction={{ label: 'Cancel', action: handleClose }}
      maxWidth="sm"
    >
      <BottomMarginContentWrapper bottomMargin="24px">
        {!viewToRename && (
          <Typography variant="EC_TYPE_BASE">
              Give a name to the current layout for easy access to these columns
              and filters.
          </Typography>
        )}
        <FormProvider {...methods}>
          <ReactHookFormsInput
            name="viewName"
            label="View Name"
            labelVariant="EC_TYPE_XS"
            labelStyles={{ color: 'GRAY_4.main', textTransform: 'uppercase' }}
            rules={{ required: 'View name is required' }}
          />
        </FormProvider>
      </BottomMarginContentWrapper>
    </BasicModal>
  );
};
